import React from 'react';
import get from 'lodash/get';
import isNil from 'lodash/isNil';

import {default as Modal, withModal} from 'components/Modal';
import BookingStatus from 'components/BookingStatus';
import Warning from 'components/Warning';
import ErrorBlock from 'components/ErrorBlock';

import {CLEANING_STATUSES_DISALLOWED_FOR_CHECK_IN} from 'ducks/allocations';
import {
  MODALS,
  fetchMemberCheckInInfo,
  checkInTenants,
  fetchPaymentSummary
} from 'ducks/frontDesk';
import {BOOKING_TYPE} from 'ducks/bookings';
import {
  openModal,
  isModalOpen,
  MODALS as BASE_MODALS,
  closeModal
} from 'ducks/modals';

import {PoidCard, PreviewDocumentModal, TakePhotoCard} from './components';

import StepSection from 'components/StepSection';

import {
  BookingType,
  Buttons,
  InfoSection,
  PaymentSummary,
  CheckList
} from 'containers/FrontDesk/components/shared';

import {formatDate} from 'utils/datetime';

import './styles.css';

class MemberCheckInModal extends React.Component<any> {
  componentDidMount() {
    const {locationRef, bookingId} = this.props.data;

    this.props.fetchMemberCheckInInfo(
      {locationRef, bookingId},
      this.fetchMemberCheckInInfoExtraActions()
    );

    this.props.fetchPaymentSummary({locationRef, bookingId}, [
      ...this.props.storeDataExtraActions(data => ({
        paymentSummary: data
      }))
    ]);
  }

  fetchMemberCheckInInfoExtraActions = () => [
    ...this.props.storeDataExtraActions(data => ({
      memberCheckInInfo: data
    }))
  ];

  checkInTenants = () => {
    const {tenancyId} = this.props.data;
    const extraActions = [
      ...this.props.closeModalExtraActions,
      ...this.props.getExtraActions(),
      () => closeModal(BASE_MODALS.CONFIRM)
    ];

    this.props.openModal(BASE_MODALS.CONFIRM, {
      heading: 'Check in member',
      question: 'Are you sure you want to check in this member?',
      confirmAction: () => this.props.checkInTenants({tenancyId}, extraActions)
    });
  };

  openPreviewPoidDocumentModal = ({poid}) => {
    this.props.openModal(MODALS.PREVIEW_POID_DOCUMENT, {poid});
  };

  render() {
    const {
      closeModal,
      data: {
        memberCheckInInfo,
        paymentSummary,
        locationRef,
        bookingId,
        isPreviewPoidDocumentModalOpen
      }
    } = this.props;

    const primaryTenant = get(memberCheckInInfo, 'primaryTenant');
    const secondaryTenant = get(memberCheckInInfo, 'secondaryTenant');

    const bookingType = get(memberCheckInInfo, 'bookingType');
    const agreementCompleted = get(memberCheckInInfo, 'agreementCompleted');
    const shouldShowWarning =
      bookingType === BOOKING_TYPE.LONG_STAY && agreementCompleted === false;

    let info: Array<any> = [];

    const cleaningStatus = get(memberCheckInInfo, 'cleaningStatus', '');
    const skipCleaningStatusCheck = get(
      memberCheckInInfo,
      'skipCleaningStatusCheck',
      ''
    );

    const cleaningStatusDisallowed =
      skipCleaningStatusCheck !== true &&
      CLEANING_STATUSES_DISALLOWED_FOR_CHECK_IN.includes(cleaningStatus);

    if (!isNil(memberCheckInInfo)) {
      const {
        roomNumber,
        roomType,
        moveIn,
        duration,
        bookingOrganisation
      } = memberCheckInInfo;

      info = [
        {header: 'Room', text: roomNumber},
        {header: 'Room Type', text: roomType},
        {header: 'Move-In', text: formatDate(moveIn)},
        {header: 'Duration', text: duration},
        {header: 'Booking Type', text: bookingType},
        {header: 'Booking Organisation', text: bookingOrganisation || 'N/A'}
      ];
    }

    const checkListItems = [
      {
        name: 'isTheRoomCleaned',
        label: 'Is the room cleaned?',
        defaultChecked: false
      },
      {
        name: 'receivedTheKeyForTheirPostBox',
        label: 'Received the key for their post box?',
        defaultChecked: false
      },
      {
        name: 'signedTheHouseRules',
        label: 'Signed the house rules?',
        defaultChecked: false
      }
    ];
    const modalType = secondaryTenant ? Modal.MEDIUM : Modal.SUB_MEDIUM;
    return (
      <React.Fragment>
        <Modal
          show={true}
          onClose={closeModal}
          type={modalType}
          className="MemberCheckInModal">
          <Modal.Header>
            <BookingType bookingType={get(memberCheckInInfo, 'bookingType')} />
            <div className="headingAndStatus">
              <h3 className="heading">
                {get(memberCheckInInfo, 'bookingType')} Check-in
              </h3>
              {memberCheckInInfo && (
                <BookingStatus status={memberCheckInInfo.bookingStatus} />
              )}
            </div>
          </Modal.Header>
          <Modal.Body>
            {memberCheckInInfo && (
              <div>
                <InfoSection info={info} />

                {cleaningStatusDisallowed && (
                  <ErrorBlock className="roomStatusError">
                    {`Allocated room is "${cleaningStatus.toLowerCase()}". Please change the room or update
                    the room housekeeping status.`}
                  </ErrorBlock>
                )}

                {shouldShowWarning && (
                  <Warning className="licenseAgreementWarning">
                    There is no completed agreement for this booking
                  </Warning>
                )}

                <StepSection
                  step="1"
                  className="takePhotoSection"
                  header="Check that the member's personal details are up to date">
                  <TakePhotoCard
                    tenant={primaryTenant}
                    locationRef={locationRef}
                    bookingId={bookingId}
                    fetchMemberCheckInInfoExtraActions={
                      this.fetchMemberCheckInInfoExtraActions
                    }
                    fetchArrivalsExtraActions={this.props.getExtraActions}
                  />
                  {secondaryTenant && (
                    <TakePhotoCard
                      tenant={secondaryTenant}
                      locationRef={locationRef}
                      bookingId={bookingId}
                      fetchMemberCheckInInfoExtraActions={
                        this.fetchMemberCheckInInfoExtraActions
                      }
                      fetchArrivalsExtraActions={this.props.getExtraActions}
                    />
                  )}
                </StepSection>

                <StepSection
                  className="IDSection"
                  step="2"
                  header="Check that the member matches the ID">
                  <PoidCard
                    tenant={primaryTenant}
                    locationRef={locationRef}
                    bookingId={bookingId}
                    fetchMemberCheckInInfoExtraActions={
                      this.fetchMemberCheckInInfoExtraActions
                    }
                    openPreviewPoidDocumentModal={
                      this.openPreviewPoidDocumentModal
                    }
                  />
                  {secondaryTenant && (
                    <PoidCard
                      tenant={secondaryTenant}
                      locationRef={locationRef}
                      bookingId={bookingId}
                      fetchMemberCheckInInfoExtraActions={
                        this.fetchMemberCheckInInfoExtraActions
                      }
                      openPreviewPoidDocumentModal={
                        this.openPreviewPoidDocumentModal
                      }
                    />
                  )}
                </StepSection>

                <StepSection step="3" header="Payment summary">
                  <div className="paymentSummary">
                    <PaymentSummary paymentSummary={paymentSummary} />
                    {!primaryTenant.activeGocardlessCustomer && (
                      <Warning>
                        The member has not yet signed up for GoCardless
                      </Warning>
                    )}
                  </div>
                </StepSection>

                <StepSection step="4" header="Check List">
                  <CheckList items={checkListItems} />
                </StepSection>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Buttons
              closeModal={closeModal}
              onSubmit={this.checkInTenants}
              active={!cleaningStatusDisallowed}
              submitting={false}
              label="Check-in"
            />
          </Modal.Footer>
        </Modal>
        {isPreviewPoidDocumentModalOpen && <PreviewDocumentModal />}
      </React.Fragment>
    );
  }
}

const mapStateToModalData = state => {
  return {
    isPreviewPoidDocumentModalOpen: isModalOpen(
      state,
      MODALS.PREVIEW_POID_DOCUMENT
    )
  };
};

const mapDispatchToProps = {
  fetchMemberCheckInInfo,
  checkInTenants,
  openModal,
  fetchPaymentSummary
};

export default withModal(
  MODALS.MEMBER_CHECK_IN,
  mapStateToModalData,
  mapDispatchToProps
)(MemberCheckInModal);
